import { Sparkles as AiIcon, MessageCircle as WaIcon, Award as CertIcon } from "lucide-react";
import { PinnedCreatives } from "@/components/ai/PinnedCreatives";

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { LOGO_URL, SOCIALS, API } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import api from "@/lib/api";
import SimulateDrawer from "@/components/SimulateDrawer";
import { FranchisePending } from "@/components/Franchise";
import { JoinLinkButton } from "@/components/JoinLinkButton";
import { GuideBookButton } from "@/components/GuideBookButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Inbox, Ticket, Users, Calculator, PhoneCall, Landmark, BarChart3, UsersRound, ChevronsUpDown,
  LogOut, Radio, Clock, Check, Facebook, Instagram, Download, Smartphone, Share2, MoreVertical, TrendingUp, Settings2, Image, MapPin, CalendarDays, Megaphone, UserCog, Crown, IdCard, Network, Route, Briefcase, Trophy,
} from "lucide-react";
import { ShoppingBag as ShopBagIcon, Store as StoreIcon } from "lucide-react";

const NAV = [
  { to: "/shop", label: "VIP Shop", icon: ShopBagIcon, testid: "nav-shop-button" },
  { to: "/shop-manager", label: "Shop Manager", icon: StoreIcon, testid: "nav-shop-manager-tab", ownerOnly: true },
  { to: "/ai-studio", label: "AI Studio", icon: AiIcon, testid: "nav-ai-studio-tab", ownerOnly: true },
  { to: "/whatsapp-groups", label: "WhatsApp Groups", icon: WaIcon, testid: "nav-wa-groups-tab", ownerOnly: true },
  { to: "/certificates", label: "Franchise Certificates", icon: CertIcon, testid: "nav-certificates-tab", ownerOnly: true },
  { to: "/telecaller", label: "Telecaller Sheet", icon: PhoneCall, testid: "nav-telecaller-tab", ownerOnly: true },
  { to: "/inbox", label: "Unified Inbox", icon: Inbox, testid: "nav-inbox-tab" },
  { to: "/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets-tab" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers-tab" },
  { to: "/passengers", label: "Passengers & Groups", icon: UsersRound, testid: "nav-passengers-tab" },
  { to: "/gallery", label: "Photo & Video Gallery", icon: Image, testid: "nav-gallery-tab" },
  { to: "/calculator", label: "Payment Calculator", icon: Calculator, testid: "nav-calculator-tab" },
  { to: "/pricing-settings", label: "Package Pricing", icon: Settings2, testid: "nav-pricing-tab", ownerOnly: true },
  { to: "/pnl", label: "Profit & Loss", icon: TrendingUp, testid: "nav-pnl-tab", ownerOnly: true },
  { to: "/accounts", label: "Accounts", icon: Landmark, testid: "nav-accounts-tab", ownerOnly: true },
  { to: "/ivr", label: "IVR & Channels", icon: PhoneCall, testid: "nav-ivr-tab" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics-tab" },
  { to: "/team", label: "Associate Partners", icon: UsersRound, testid: "nav-team-tab", hideCoworker: true },
  { to: "/network", label: "Member Tree", icon: Network, testid: "nav-network-tab" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard-tab" },
  { to: "/commission", label: "Commission", icon: TrendingUp, testid: "nav-commission-tab" },
  { to: "/profile", label: "My Profile", icon: UserCog, testid: "nav-profile-tab" },
  { to: "/my-designs", label: "My Flyers & Certificate", icon: AiIcon, testid: "nav-my-designs-tab" },
  { to: "/owners", label: "Company Panel", icon: Crown, testid: "nav-owners-tab", ownerOnly: true },
  { to: "/staff", label: "Office Staff", icon: IdCard, testid: "nav-staff-tab" },
  { to: "/groups", label: "Groups & Dates", icon: CalendarDays, testid: "nav-groups-tab" },
  { to: "/jobs-panel", label: "Jobs & Employment", icon: Briefcase, testid: "nav-jobs-tab" },
  { to: "/muallems", label: "Muallem List", icon: UserCog, testid: "nav-muallems-tab" },
  { to: "/pilgrim-care", label: "Pilgrim Care", icon: Route, testid: "nav-care-tab" },
  { to: "/flyer", label: "Marketing Flyer", icon: Megaphone, testid: "nav-flyer-tab" },
  { to: "/offices", label: "Office Locations", icon: MapPin, testid: "nav-offices-tab", ownerOnly: true },
];

const STATUS_COLORS = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  break: "bg-blue-500",
  offline: "bg-gray-500",
};

function isAfterHours(ws) {
  if (!ws?.business_hours) return false;
  const bh = ws.business_hours;
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const dow = (now.getDay() + 6) % 7; // Mon=0
  if (!(bh.days || [0, 1, 2, 3, 4, 5]).includes(dow)) return true;
  return !(now.getHours() >= (bh.start ?? 9) && now.getHours() < (bh.end ?? 21));
}

export default function Layout({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const { workspaces, current, switchWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(user?.status || "available");
  const [simOpen, setSimOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const isRunningStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(!!isRunningStandalone);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setDeferredPrompt(null);
      }
    } else {
      setInstallModalOpen(true);
    }
  };

  const afterHours = isAfterHours(current);

  const changeStatus = async (s) => {
    setStatus(s);
    await api.put(`/team/status?status=${s}`);
    refreshUser();
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r border-border/60 bg-[#031812]/80 backdrop-blur-xl hidden md:flex flex-col">
        <div className="py-5 px-4 flex flex-col items-center text-center gap-2 border-b border-border/60 bg-gradient-to-b from-primary/30 to-transparent">
          <img
            src={LOGO_URL}
            alt="Bengal Umrah House"
            data-testid="sidebar-logo"
            className="w-full max-w-[228px] h-auto object-contain rounded-xl ring-1 ring-gold/50 shadow-lg"
          />
          <div className="leading-tight mt-1">
            <p className="font-serif text-base font-semibold text-white tracking-wide">Bengal Umrah House</p>
            <p className="eyebrow text-gold/90 text-[10px]">Prime Care Console</p>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
          {NAV.filter((n) => (!n.ownerOnly || user?.role === "owner") && (!n.hideCoworker || user?.role !== "coworker")).map((n) => {
            const active = location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <button
                key={n.to}
                data-testid={n.testid}
                onClick={() => navigate(n.to)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? "bg-primary/40 text-white gold-glow border border-gold/30"
                    : "text-muted-foreground hover:text-white hover:bg-primary/20"
                }`}
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </button>
            );
          })}
          <JoinLinkButton />
          <GuideBookButton testid="nav-guide-download"
            href={`${API}/prospectus/guide.pdf?origin=${encodeURIComponent(window.location.origin)}${user?.referral_code ? `&ref=${user.referral_code}` : ""}`} />
        </nav>
        <div className="p-3 border-t border-border/60 space-y-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${afterHours ? "text-amber-400 bg-amber-500/10" : "text-emerald-400 bg-emerald-500/10"}`}>
            <Clock className="w-3.5 h-3.5" />
            {afterHours ? "AFTER-HOURS · Auto-reply on" : "OPEN · Mon–Sat 9–21 IST"}
          </div>
          <div className="flex items-center gap-2 px-1">
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="sidebar-facebook-link"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-card/40 hover:bg-gold/15 border border-border/60 hover:border-gold/40 text-xs text-muted-foreground hover:text-gold transition-colors">
              <Facebook className="w-3.5 h-3.5" /> Facebook
            </a>
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="sidebar-instagram-link"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-card/40 hover:bg-gold/15 border border-border/60 hover:border-gold/40 text-xs text-muted-foreground hover:text-gold transition-colors">
              <Instagram className="w-3.5 h-3.5" /> Instagram
            </a>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex-shrink-0 flex items-center justify-between px-4 sm:px-6 border-b border-border/60 glass sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="logo"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-gold/50 shadow md:hidden"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="workspace-selector-trigger"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/70 bg-card/50 hover:border-gold/50 transition-colors">
                  <span className="w-2.5 h-2.5 rounded-full bg-gold" />
                  <span className="text-sm font-medium max-w-[180px] truncate">{current?.name || "Workspace"}</span>
                  <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="eyebrow text-gold">Workspaces</DropdownMenuLabel>
                {workspaces.map((w) => (
                  <DropdownMenuItem key={w.id} data-testid={`workspace-option-${w.slug}`} onClick={() => w.id !== current?.id && switchWorkspace(w)}>
                    <span className="flex-1 truncate">{w.name}</span>
                    {w.id === current?.id && <Check className="w-4 h-4 text-gold" />}
                  </DropdownMenuItem>
                ))}
                {user?.role === "owner" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                      + Al-Fattah added in Phase 2
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge variant="outline" className="hidden sm:inline-flex border-gold/40 text-gold eyebrow">
              {current?.theme?.tag || "PRIME CONSOLE"}
            </Badge>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {!isStandalone && (
              <Button
                data-testid="install-app-btn"
                onClick={handleInstallClick}
                variant="outline"
                size="sm"
                className="border-gold/40 text-gold hover:bg-gold/15 gap-1.5 font-medium"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Install App</span>
              </Button>
            )}

            <Button data-testid="simulate-inbound-trigger" onClick={() => setSimOpen(true)}
              className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2" size="sm">
              <Radio className="w-4 h-4" /> <span className="hidden sm:inline">Simulate Inbound</span>
            </Button>

            <Select value={status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="agent-availability-toggle" className="w-[130px] h-9 bg-card/50 border-border/70">
                <span className={`w-2 h-2 rounded-full mr-1 ${STATUS_COLORS[status]}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="busy">Busy on Call</SelectItem>
                <SelectItem value="break">On Break</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="w-9 h-9 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-sm font-semibold text-gold">
                  {user?.name?.[0] || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal capitalize">{user?.role} · {user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <PageBackground />
        <main className="relative z-10 flex-1 min-h-0 overflow-auto">{user?.role === "agent" && user?.approval === "pending" ? <FranchisePending onLogout={logout} /> : <><PinnedCreatives className="px-4 sm:px-8 pt-4" />{children}</>}</main>
      </div>

      <SimulateDrawer open={simOpen} onOpenChange={setSimOpen} />

      <Dialog open={installModalOpen} onOpenChange={setInstallModalOpen}>
        <DialogContent className="bg-[#05261D] border-gold/30 text-white max-w-md" data-testid="install-guide-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2 text-lg">
              <Smartphone className="w-5 h-5 text-gold" /> Install Bengal Umrah App
            </DialogTitle>
            <DialogDescription className="text-emerald-100/70 text-xs">
              Add Bengal Umrah House Care Console directly to your phone’s Home Screen as an app.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2 text-sm">
            <div className="p-3.5 rounded-lg bg-card/60 border border-border/70">
              <p className="font-semibold text-gold flex items-center gap-2 text-xs uppercase tracking-wider mb-2">
                <MoreVertical className="w-4 h-4" /> On Android (Google Chrome)
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-muted-foreground">
                <li>Tap the <strong className="text-white">3 dots menu (⋮)</strong> in Chrome (top-right).</li>
                <li>Tap <strong className="text-white">"Install app"</strong> or <strong className="text-white">"Add to Home screen"</strong>.</li>
                <li>Tap <strong className="text-white">Install</strong>. The icon will appear on your phone screen!</li>
              </ol>
            </div>

            <div className="p-3.5 rounded-lg bg-card/60 border border-border/70">
              <p className="font-semibold text-gold flex items-center gap-2 text-xs uppercase tracking-wider mb-2">
                <Share2 className="w-4 h-4" /> On iPhone / iPad (Safari)
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-muted-foreground">
                <li>Tap the <strong className="text-white">Share icon</strong> (square with arrow up at bottom).</li>
                <li>Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong>.</li>
                <li>Tap <strong className="text-white">Add</strong> in the top right corner.</li>
              </ol>
            </div>
          </div>

          <Button onClick={() => setInstallModalOpen(false)} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold" data-testid="close-install-modal-btn">
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
